<template>
	<div>
		<main class="ncd-main-container mob-d-block d-flex" v-if="isLaptop">
			<section id="hideinmobile" class="ncd-left-section invisible-scroll">
				<MainLeftpart :expanded="true" />
			</section>
			<section
				style="width: calc(100% - (20% + 240px))"
				class="ncd-middle-container mob-w-100 d-flex align-items-center gap-24px flex-column overflow-y-auto invisible-scroll mobpaddingx16px mt-16px lappadding-30px"
			>
				<!-- Drawn once, in the final order: nothing reorders in front of the candidate. -->
				<template v-if="layoutReady">
					<topCards :card-order="dashboardTopCardOrder" />
					<Banner :banners="dashboardBanners" />
					<ProgressHub />
					<component
						v-for="key in orderedMiddleSections"
						:key="key"
						:is="middleSectionComponent(key)"
					/>
				</template>
				<div v-else class="ncd-layout-placeholder w-100" aria-busy="true" aria-label="Loading your dashboard">
					<div class="ncd-placeholder-block ncd-placeholder-block--cards" />
					<div class="ncd-placeholder-block ncd-placeholder-block--banner" />
				</div>
			</section>
			<section
				id="hideinmobile"
				class="ncd-right-section w-20 invisible-scroll"
			>
				<MainRightpart />
			</section>
		</main>
		<main v-else>
			<NcdTopContainer />
			<section
				class="ncd-middle-container overflow-y-auto invisible-scroll mobpaddingx16px"
			>
				<template v-if="layoutReady">
					<topCards :card-order="dashboardTopCardOrder" />
					<Banner class="mobmt-24px" :banners="dashboardBanners" />
					<ProgressHub class="mobmt-24px" />
					<MainRightpart class="mobmt-24px w-100" />
					<component
						v-for="key in orderedMiddleSections"
						:key="key"
						:is="middleSectionComponent(key)"
						class="mobmt-24px"
					/>
				</template>
				<div v-else class="ncd-layout-placeholder w-100" aria-busy="true" aria-label="Loading your dashboard">
					<div class="ncd-placeholder-block ncd-placeholder-block--cards" />
					<div class="ncd-placeholder-block ncd-placeholder-block--banner" />
				</div>
			</section>
			<NcdBottomContainer />
		</main>

		<!-- Instant Interview Coming Soon Modal -->
		<PaymentComingSoonModal
			:showModal.sync="showPaymentComingSoonModal"
			:fromInstantInterview="true"
		/>
	</div>
</template>

<script>
import Banner from "@/components/LandingPage/Banner.vue";
import mobHeader from "@/components/fixedcomponents/mobileHeader.vue";
import MainLeftpart from "@/components/LandingPage/MainLeftpart.vue";
import MainRightpart from "@/components/LandingPage/MainRightpart.vue";
import AiMockInterview from "@/components/LandingPage/AiMockInterview.vue";
import "@/assets/globalcss/landingpage.css";
import topCards from "@/components/LandingPage/topCards.vue";
import Courses from "@/components/MyProfile/Courses.vue";
import Jobs from "@/components/MyProfile/Jobs.vue";
import ProgressHub from "@/components/MyProfile/ProgressHub/ProgressHub.vue";

import PaymentComingSoonModal from "@/views/MyProfile/PaymentComingSoonModal.vue";
import NcdTopContainer from "@/components/NewCandidateDashboard/NcdTopContainer.vue";
import NcdBottomContainer from "@/components/NewCandidateDashboard/NcdBottomContainer.vue";
import { getPriorityList } from "@/utils/apiFunctions";
import { applyOrder } from "@/utils/applyOrder";

const MIDDLE_SECTION_COMPONENTS = {
	jobs: "Jobs",
	ai_mock_interview: "AiMockInterview",
	courses: "Courses",
};

// The backend gives the pipeline 2 s; past this the default order is drawn and a late answer is ignored.
const LAYOUT_WAIT_MS = 2500;

function byPriority(list) {
	const rank = (item) =>
		typeof item.priority === "number" ? item.priority : Number.MAX_SAFE_INTEGER;
	return list.filter(Boolean).sort((a, b) => rank(a) - rank(b));
}

function typesByPriority(list) {
	return Array.isArray(list) ? byPriority(list).map((item) => item.type) : null;
}

export default {
	components: {
		topCards,
		AiMockInterview,
		Courses,
		Jobs,
		ProgressHub,
		MainLeftpart,
		MainRightpart,
		Banner,
		PaymentComingSoonModal,
		NcdTopContainer,
		NcdBottomContainer,
	},
	computed: {
		dashboardTopCardOrder() {
			return typesByPriority(this.dashboardView?.topCards);
		},
		dashboardBanners() {
			return Array.isArray(this.dashboardView?.banners) ? this.dashboardView.banners : null;
		},
		orderedMiddleSections() {
			const sections = Array.isArray(this.dashboardView?.sections)
				? this.dashboardView.sections
				: [];
			return applyOrder(
				Object.keys(MIDDLE_SECTION_COMPONENTS),
				byPriority(sections).map((s) => s.section)
			);
		},
	},
	data() {
		return {
			isLaptop: true,
			dashboardView: null,
			layoutReady: false,
		};
	},
	mounted() {
		this.checkScreenSize();
		this.fetchDashboardView();
		window.addEventListener("resize", this.checkScreenSize);
	},
	beforeDestroy() {
		clearTimeout(this.layoutWaitTimer);
		window.removeEventListener("resize", this.checkScreenSize);
	},
	methods: {
		checkScreenSize() {
			this.isLaptop = window.innerWidth >= 1024;
		},
		middleSectionComponent(key) {
			return MIDDLE_SECTION_COMPONENTS[key];
		},
		async fetchDashboardView() {
			this.layoutWaitTimer = setTimeout(() => {
				this.layoutReady = true;
			}, LAYOUT_WAIT_MS);
			try {
				const res = await getPriorityList();
				// Too late: the default order is already on screen and must not move.
				if (!this.layoutReady && res && res.status === 200) {
					this.dashboardView = res.data?.data || res.data || null;
				}
			} catch (err) {
				console.error("Error fetching dashboard view:", err);
			} finally {
				clearTimeout(this.layoutWaitTimer);
				this.layoutReady = true;
			}
		},
	},
};
</script>

<style scoped>
.ncd-layout-placeholder {
	display: flex;
	flex-direction: column;
	gap: 24px;
}
.ncd-placeholder-block {
	border-radius: 15px;
	background: #eef2f6;
}
.ncd-placeholder-block--cards {
	height: 96px;
}
.ncd-placeholder-block--banner {
	height: 165px;
}
.ncd-main-container {
	max-width: 1950px;
	width: 100%;
	margin: 0 auto;
	position: relative;
	top: 60px;
	height: calc(100vh - 60px);
	justify-content: center;
}
.ncd-left-section {
	padding: 16px;
	border-radius: 0 24px 24px 0;
	border-right: 1px solid #e2e8f0;
	box-shadow: 3px 0 25px -20px rgba(0, 0, 0, 0.3);
	overflow-y: auto;
}

.ncd-right-section {
	padding: 16px;
	border-left: 1px solid #e2e8f0;
	border-radius: 24px 0 0 24px;
	box-shadow: -3px 0 25px -20px rgba(0, 0, 0, 0.3);
	overflow-y: auto;
}
.gap-30px {
	gap: 30px;
}
.gap-24px {
	gap: 24px;
}

/* Responsive Helper Utilities */
@media (max-width: 1028px) {
	#hideinmobile {
		display: none !important;
	}
}
@media (min-width: 1029px) {
	#hideinlaptop {
		display: none !important;
	}
}

/* Mobile Search Overlay (max-width: 1028px) */
@media only screen and (max-width: 1028px) {
	.search-suggestion-container {
		position: fixed;
		top: calc(0px + env(safe-area-inset-top));
		left: 0;
		width: 100vw;
		max-width: 100vw;
		height: 100vh;
		z-index: 2069;
		background: #f7f7f7;
		overflow-x: hidden;
		overflow-y: auto;
	}
	.search-suggestion-container .header-center-area {
		position: relative;
		width: 100%;
		padding: 15px 5px;
		pointer-events: auto;
		height: fit-content;
	}
	.search-suggestion-container .search-tabs-container {
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid #c4dcf6;
		border-radius: 74px;
		padding: 5px 6px;
		gap: 15px;
		background-color: white;
		margin-bottom: 10px;
		opacity: unset;
		width: 95%;
		left: 10px;
		transform: unset;
		height: unset;
		margin-left: auto;
		margin-right: auto;
	}
	.search-suggestion-container .search-tab {
		font-size: 16px;
		color: #4a7eff;
		cursor: pointer;
		font-weight: 400;
		padding: 4px;
		border-radius: 30px;
		transition: all 0.2s;
		line-height: 2;
	}
	.search-suggestion-container .search-tab.active {
		background: #4a7eff;
		color: white;
	}
	.search-suggestion-container .divider {
		color: #c4dcf6;
	}
	.search-suggestion-container .header-search {
		border: 1.5px solid #efefef;
		border-radius: 74px;
		position: relative;
		width: 100%;
		padding: 10px;
		background: white;
		flex: 1;
		box-shadow: 0px 4px 2px rgba(0, 0, 0, 0.02), 0px 2px 2px rgba(0, 0, 0, 0.03),
			0px 0px 1px rgba(0, 0, 0, 0.03);
	}
	.search-suggestion-container .header-search img {
		position: absolute;
		left: 19px;
		top: 14px;
		width: 16px;
		height: 16px;
	}
	.search-suggestion-container .header-search .form-control {
		border: none;
		padding: 0.375rem 0.75rem 0.375rem 42px;
		border-radius: 50px;
	}
	.search-suggestion-container .header-search .form-control:focus {
		box-shadow: none;
	}
	.search-suggestion-container .search-bar-wrapper {
		position: fixed;
		padding: 15px;
		background-color: #ffffff;
		pointer-events: auto;
		box-shadow: 133px 38px 56px rgba(0, 0, 0, 0.01),
			75px 22px 47px rgba(0, 0, 0, 0.05), 33px 10px 35px rgba(0, 0, 0, 0.09),
			8px 2px 19px rgba(0, 0, 0, 0.1);
		border-radius: 20px 20px 0 0;
	}
	.search-suggestion-container .search-bar-wrapper.is-active {
		top: calc(78px + env(safe-area-inset-top));
		max-width: 95%;
		width: 95%;
		left: 11px;
	}
	.search-suggestion-container .search-dropdown-content {
		width: 100%;
		padding: 0px;
		padding-right: 2px;
		margin-top: -1px;
		box-shadow: none;
		min-height: unset;
		max-height: calc(100dvh - 168px);
		overflow: hidden auto;
		border-radius: 0 0 20px 20px;
	}
}

/* Desktop Search Overlay (min-width: 1029px) */
@media (min-width: 1029px) {
	.search-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background: rgba(0, 0, 0, 0.15);
		backdrop-filter: blur(6px);
		-webkit-backdrop-filter: blur(6px);
		z-index: 1050;
	}
	.hp-whitecross-pos {
		position: absolute;
		right: 25px;
		top: 20px;
		z-index: 2070;
	}
	.search-suggestion-container {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		z-index: 1060;
		pointer-events: auto;
	}
	.search-suggestion-container .search-tabs-container {
		display: flex;
		align-items: center;
		justify-content: center;
		border: 1px solid #c4dcf6;
		border-radius: 74px;
		padding: 4px 6px;
		gap: 15px;
		background-color: white;
		position: fixed;
		top: 16px;
		left: 50%;
		transform: translateX(-50%);
		height: 44px;
		z-index: 1062;
		box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
	}
	.search-suggestion-container .search-tab {
		background: transparent;
		border: none;
		font-family: "Inter", sans-serif;
		font-size: 15px;
		color: #4a7eff;
		cursor: pointer;
		font-weight: 500;
		padding: 4px 16px;
		border-radius: 30px;
		transition: all 0.2s;
	}
	.search-suggestion-container .search-tab.active {
		background: #4a7eff;
		color: white;
	}
	.search-suggestion-container .divider {
		color: #c4dcf6;
	}
	.search-suggestion-container .search-bar-wrapper {
		position: fixed;
		top: 72px;
		left: 50%;
		transform: translateX(-50%);
		width: 90%;
		max-width: 650px;
		z-index: 1061;
		display: flex;
		flex-direction: column;
		align-items: center;
	}
	.search-suggestion-container .header-search {
		border: 1.5px solid #efefef;
		box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
		border-radius: 74px;
		position: relative;
		width: 100%;
		height: 42px;
		background: white;
		display: flex;
		align-items: center;
	}
	.search-suggestion-container .header-search .form-control {
		border: none;
		padding: 0.375rem 0.75rem 0.375rem 44px;
		border-radius: 50px;
		background: transparent;
		font-size: 14px;
		height: 40px;
	}
	.search-suggestion-container .header-search .form-control:focus {
		box-shadow: none;
	}
	.search-suggestion-container .header-search img {
		position: absolute;
		left: 16px;
		top: 50%;
		transform: translateY(-50%);
		width: 16px;
		height: 16px;
	}
	.search-suggestion-container .search-dropdown-content {
		width: 100%;
		margin-top: 8px;
		background: #ffffff;
		border-radius: 20px;
		box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.12);
		max-height: calc(100vh - 140px);
		overflow-y: auto;
	}
}

@media only screen and (min-width: 1028px) {
	.lappadding-30px {
		padding: 0 30px;
	}
}
@media only screen and (max-width: 1028px) {
	.ncd-main-container {
		max-width: 100%;
		top: 0px;
		height: 100dvh;
	}
	.mob-d-block {
		display: block !important;
	}
	.ncd-middle-container {
		padding-top: 20px;
		height: calc(
			100dvh - (215px + env(safe-area-inset-bottom) + env(safe-area-inset-top))
		);
		/* Stop the browser from re-anchoring the scroll position when section
		   content resizes (async images loading, skeleton→card swaps). Without
		   this, cards visually jump up/down while the user is scrolling. */
		overflow-anchor: none;
	}
	.mobmt-24px {
		margin-top: 24px;
	}
}
</style>
